/**
 * IncomingCallOverlayProvider — global RC incoming-call popup (real mode only).
 *
 * Why global?
 *   `RCSoftphone` already listens to RC Embeddable `postMessage` events, but
 *   only while its iframe is mounted (Communications / Sales Dialer pages).
 *   To surface inbound calls on Dashboard / Leads / any route, we mount a
 *   second HIDDEN RC iframe at the app root. Both iframes share the same RC
 *   user session (cookies) and both emit events — we dedupe on the popup side
 *   by call / session id so the user only ever sees one overlay per ring.
 *
 * Strict scope:
 *   • Real users only. `useIsDemo() === true` → returns null and the hidden
 *     iframe is never mounted. No demo UX change.
 *   • Public routes (/login, /book-demo, /sign) never see the overlay — the
 *     provider is only rendered inside `<MainLayout>`.
 *   • Event capture + compact popup only. Does NOT render the full dialer.
 *   • On inbound ring, hits `GET /api/integrations/rc/match-inbound?phone=…`
 *     to resolve lead/contact and deep-link "Open Lead".
 */
import { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Phone, X, ExternalLink, User } from "lucide-react";
import { useAuth, API } from "@/App";
import axios from "axios";

const EMBED_URL = "https://apps.ringcentral.com/integration/ringcentral-embeddable/latest/app.html";

const IncomingCallContext = createContext(null);
export const useIncomingCall = () => useContext(IncomingCallContext);

/**
 * Extract a stable de-dupe key for an RC event. RC Embeddable call payloads
 * include `sessionId` and/or `telephonySessionId`. We fall back to the
 * remote-party phone so bursts of repeated ring events without an id still
 * produce a single popup (RC fires multiple `rc-call-ring-notify` for the
 * same call as SIP negotiates).
 */
function callKey(call) {
  if (!call || typeof call !== "object") return null;
  return (
    call.sessionId ||
    call.telephonySessionId ||
    call.id ||
    call.partyId ||
    call.from?.phoneNumber ||
    call.from?.extensionNumber ||
    null
  );
}

function callerDisplay(call) {
  if (!call || typeof call !== "object") return { name: "", phone: "" };
  const phone = call.from?.phoneNumber || call.from?.extensionNumber || "";
  const name = call.from?.name || "";
  return { name, phone };
}

function isInbound(call) {
  const d = (call?.direction || "").toString().toLowerCase();
  return d === "inbound" || d === "in";
}

export function IncomingCallOverlayProvider({ children }) {
  const { user, token } = useAuth() || {};
  const authed = !!user;

  // Active inbound ring state (single). `null` when idle.
  // `key`: dedupe key. `call`: raw RC payload. `match`: resolved lead/contact or null.
  // `dismissed` keys live in a ref so re-renders don't wipe them.
  const [incoming, setIncoming] = useState(null);
  const dismissedRef = useRef(new Set());
  const lastKeyRef = useRef(null);

  const navigate = useNavigate();

  // Resolve phone → lead/contact (best effort, never throws into the UI).
  const resolveMatch = useCallback(async (phone) => {
    if (!phone || !token) return null;
    try {
      const r = await axios.get(`${API}/integrations/rc/match-inbound`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { phone },
        timeout: 4000,
      });
      return r.data?.match || null;
    } catch {
      return null;
    }
  }, [token]);

  // Single window listener — both visible (RCSoftphone) and hidden (global)
  // iframes post into the same `window`, so one handler catches everything.
  // Dedupe by call key; dismissed keys cannot re-ring the popup.
  useEffect(() => {
    if (!authed) return;
    function onMsg(ev) {
      const data = ev?.data;
      if (!data || typeof data !== "object") return;
      const t = data.type;
      const call = data.call || {};
      const key = callKey(call);

      if (t === "rc-call-ring-notify") {
        if (!isInbound(call)) return;
        if (!key) return;
        if (dismissedRef.current.has(key)) return;
        if (lastKeyRef.current === key) return; // already showing
        lastKeyRef.current = key;
        const { name, phone } = callerDisplay(call);
        setIncoming({ key, call, name, phone, match: null, resolving: true });
        resolveMatch(phone).then((match) => {
          setIncoming((prev) => (prev && prev.key === key ? { ...prev, match, resolving: false } : prev));
        });
      } else if (t === "rc-call-start-notify" || t === "rc-call-end-notify") {
        // Auto-close on answer (start) or hangup (end).
        if (key && lastKeyRef.current === key) {
          lastKeyRef.current = null;
          setIncoming(null);
        } else if (!key) {
          // Missing key — close any open popup defensively.
          lastKeyRef.current = null;
          setIncoming(null);
        }
      }
    }
    window.addEventListener("message", onMsg);
    return () => window.removeEventListener("message", onMsg);
  }, [authed, resolveMatch]);

  const dismiss = useCallback(() => {
    if (incoming?.key) dismissedRef.current.add(incoming.key);
    lastKeyRef.current = null;
    setIncoming(null);
  }, [incoming]);

  const openCommunications = useCallback(() => {
    dismiss();
    navigate("/communications?tab=phone");
  }, [navigate, dismiss]);

  const openLead = useCallback(() => {
    const leadId = incoming?.match?.lead_id;
    dismiss();
    if (leadId) navigate(`/lead-focus/${leadId}`);
  }, [navigate, dismiss, incoming]);

  const ctx = useMemo(() => ({ incoming, dismiss }), [incoming, dismiss]);

  return (
    <IncomingCallContext.Provider value={ctx}>
      {children}
      {authed && <GlobalRCBridge />}
      {incoming && (
        <IncomingCallPopup
          incoming={incoming}
          onDismiss={dismiss}
          onOpenCommunications={openCommunications}
          onOpenLead={openLead}
        />
      )}
    </IncomingCallContext.Provider>
  );
}

/**
 * Hidden RC iframe kept mounted at app root so inbound ring events flow even
 * when the user is not on Communications / Sales Dialer. The iframe auto-
 * connects to the same RC user session the visible widget uses — no double
 * login. Visually zero-sized; focus-trap safe (tabIndex=-1).
 */
function GlobalRCBridge() {
  const location = useLocation();
  // Avoid double-mounting when the user is on Communications — RCSoftphone's
  // visible iframe is already feeding events into the same window. Sales
  // Dialer too. This keeps us at a single iframe during those sessions.
  const visibleRoutes = ["/communications", "/dialer"];
  const visibleIsMounted = visibleRoutes.some((p) => location.pathname === p || location.pathname.startsWith(p + "/"));
  if (visibleIsMounted) return null;
  return (
    <iframe
      data-testid="rc-global-bridge"
      title="RingCentral incoming-call bridge"
      src={EMBED_URL}
      aria-hidden="true"
      tabIndex={-1}
      allow="microphone"
      style={{
        position: "fixed", width: 1, height: 1, border: 0,
        bottom: -9999, right: -9999, opacity: 0, pointerEvents: "none",
      }}
    />
  );
}

function IncomingCallPopup({ incoming, onDismiss, onOpenCommunications, onOpenLead }) {
  const { name, phone, match, resolving } = incoming;
  const display = match?.name || name || phone || "Unknown caller";
  const sub = match?.company || (match && phone) || (name ? phone : "");
  const canOpenLead = !!match?.lead_id;
  return (
    <div
      data-testid="incoming-call-popup"
      role="dialog"
      aria-live="assertive"
      aria-label="Incoming call"
      style={{
        position: "fixed", right: 20, bottom: 20, zIndex: 9999,
        width: 320, background: "#ffffff", color: "var(--text-primary)",
        border: "0.5px solid var(--border, #dddddd)", borderRadius: 12,
        boxShadow: "0 12px 32px rgba(0,0,0,0.18)",
        padding: 14, fontSize: 13,
        animation: "incomingCallSlideIn 220ms ease-out",
      }}
    >
      <style>{`@keyframes incomingCallSlideIn { from { transform: translateY(12px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }`}</style>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <div style={{
          width: 34, height: 34, borderRadius: "50%", background: "#e6f1fb",
          display: "flex", alignItems: "center", justifyContent: "center",
          color: "#1A73E8", flexShrink: 0,
        }}>
          <Phone style={{ width: 17, height: 17 }} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 10, fontWeight: 600, letterSpacing: 0.6,
                        color: "#1A73E8", textTransform: "uppercase", marginBottom: 2 }}>
            Incoming call{resolving ? " · resolving…" : ""}
          </div>
          <div data-testid="incoming-call-display"
               style={{ fontSize: 14, fontWeight: 600, overflow: "hidden",
                        textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {display}
          </div>
          {sub && sub !== display && (
            <div style={{ fontSize: 11, color: "var(--text-tertiary)",
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
              {sub}
            </div>
          )}
        </div>
        <button
          data-testid="incoming-call-dismiss"
          onClick={onDismiss}
          aria-label="Dismiss"
          style={{ background: "none", border: "none", cursor: "pointer",
                   color: "var(--text-tertiary)", padding: 4 }}
        >
          <X style={{ width: 14, height: 14 }} />
        </button>
      </div>
      <div style={{ display: "flex", gap: 6 }}>
        <button
          data-testid="incoming-call-open-comms"
          onClick={onOpenCommunications}
          style={{
            flex: 1, background: "#2563eb", color: "#fff", border: "none",
            borderRadius: 8, padding: "8px 10px", fontSize: 12, fontWeight: 600,
            cursor: "pointer", display: "flex", alignItems: "center",
            justifyContent: "center", gap: 6,
          }}
        >
          <ExternalLink style={{ width: 12, height: 12 }} /> Open Communications
        </button>
        {canOpenLead && (
          <button
            data-testid="incoming-call-open-lead"
            onClick={onOpenLead}
            style={{
              background: "#fff", color: "#2563eb",
              border: "0.5px solid #2563eb", borderRadius: 8, padding: "8px 10px",
              fontSize: 12, fontWeight: 600, cursor: "pointer",
              display: "flex", alignItems: "center", gap: 6,
            }}
          >
            <User style={{ width: 12, height: 12 }} /> Open Lead
          </button>
        )}
      </div>
    </div>
  );
}
